import { ProjectState } from '../../../dtos/project-state';

export interface GuideSectionContent {
  id: string;
  heading: string;
  shortHelp: string;
  whyItMatters?: string;
  howItWorks?: string[];
  prismaMapping?: string;
  nextStage?: string;
}

export interface GuideStageContent {
  stage: ProjectState;
  label: string;
  sections: GuideSectionContent[];
}

export const GUIDE_CONTENT: GuideStageContent[] = [
  {
    stage: ProjectState.SETUP,
    label: 'Setup & Ingestion',
    sections: [
      {
        id: 'screening-criteria',
        heading: 'Screening Criteria',
        shortHelp:
          'Screening criteria are the rules that determine whether a paper qualifies for your review. Inclusion criteria define what must be true for a paper to be included (e.g. "Published in a peer-reviewed journal"). Exclusion criteria define conditions that disqualify a paper even if all inclusion criteria are met (e.g. "Full text not available"). Screeners apply these criteria when deciding Include or Exclude. They are NOT the same as exclusion reasons — criteria are the rules, reasons are the justifications screeners select after deciding to exclude.',
        whyItMatters:
          'Screening criteria are the backbone of a reproducible systematic review. They operationalise your research question into testable rules that every screener can apply consistently. Without clear criteria, different screeners may include different papers, undermining the validity of your review.',
        howItWorks: [
          'Define inclusion criteria (e.g. "Published in peer-reviewed journal", "Written in English")',
          'Define exclusion criteria (e.g. "Full text not available", "Duplicate publication")',
          'Each criterion is a single statement that can be answered with Yes or No for a given paper',
          'A paper must meet ALL inclusion criteria and NONE of the exclusion criteria to pass screening',
          'Criteria are locked once screening begins to prevent bias mid-review',
        ],
        prismaMapping:
          'Screening criteria correspond to the "Eligibility criteria" item in the PRISMA 2020 checklist (Item 5) and feed into the flow diagram from "Records screened" onward.',
        nextStage: 'Title & Abstract Screening',
      },
      {
        id: 'exclusion-reasons',
        heading: 'Exclusion Reasons',
        shortHelp:
          'Exclusion reasons are the predefined labels that screeners choose from when they exclude a paper. They answer the question "Why was this paper excluded?" (e.g. "Wrong population", "Wrong study design", "Outcome not reported"). They are NOT the same as screening criteria — criteria are the rules that guide the decision, reasons are the justifications recorded after the decision. Every excluded paper must have at least one reason assigned, and these reasons feed directly into the PRISMA flow diagram.',
        whyItMatters:
          'Recording why each paper was excluded is a PRISMA 2020 requirement (Item 9). Structured reasons allow the review team to analyse patterns in exclusion, produce an accurate PRISMA flow diagram, and justify every decision in the final report.',
        howItWorks: [
          'Create a list of reasons (e.g. "Wrong population", "Wrong study design", "Outcome not reported")',
          'Reasons appear as a dropdown during screening for both title/abstract and full-text stages',
          'Each excluded paper must have at least one reason assigned',
          'The PRISMA flow diagram counts exclusions by reason at each stage',
        ],
        prismaMapping:
          'Exclusion reasons map to the "Reasons for exclusion" section of the PRISMA 2020 flow diagram. Journals typically require these to be listed in the manuscript.',
      },
      {
        id: 'protocol',
        heading: 'Systematic Review Protocol',
        shortHelp:
          "The protocol documents your planned methodology before the review begins: research questions, search strategy, and the Cohen's Kappa threshold. It serves as a blueprint and a record of your intended methods.",
        whyItMatters:
          'Pre-registering or documenting your protocol prevents cherry-picking of results and distinguishes planned analyses from post-hoc changes. Many journals now require protocol registration or submission alongside the manuscript.',
        howItWorks: [
          'Define your research questions: the precise questions the review aims to answer',
          'Document your search methodology: the databases, search strings, and date ranges used',
          "Set the Cohen's Kappa threshold: the minimum inter-rater agreement your team will accept",
          'The protocol is visible throughout the project and can be published when the review completes',
        ],
        prismaMapping:
          'The protocol covers PRISMA 2020 checklist Items 4 (Protocol & Registration), 5 (Eligibility criteria), 6 (Information sources), and 7 (Search strategy).',
      },
      {
        id: 'importing-papers',
        heading: 'Importing Papers',
        shortHelp:
          "Scholaris supports multiple ways to build your paper corpus: search the OpenAlex database, upload BibTeX files, upload PDFs directly, or copy papers from your group's shared corpus. Deduplication runs automatically.",
        whyItMatters:
          'A comprehensive literature search is the foundation of any systematic review. Scholaris centralises discovery from multiple sources so your team starts with a complete, deduplicated record set ready for screening.',
        howItWorks: [
          'OpenAlex Search: query the OpenAlex academic database by keyword, author, DOI, or year range',
          'BibTeX Import: upload a .bib file exported from Zotero, EndNote, or any reference manager',
          'PDF Upload: drag-and-drop PDFs; Scholaris extracts metadata via GROBID where available',
          'Group Corpus: reuse papers already imported in your research group',
          'Track import progress in real time with an expected-vs-processed counter',
          'Automatic deduplication keeps your corpus clean',
        ],
        prismaMapping:
          'Paper import corresponds to the "Identification" phase of the PRISMA 2020 flow diagram: records identified from databases, registers, and other sources.',
      },
    ],
  },
  {
    stage: ProjectState.TITLE_ABSTRACT_SCREENING,
    label: 'Title & Abstract Screening',
    sections: [
      {
        id: 'title-abstract-screening',
        heading: 'Title & Abstract Screening',
        shortHelp:
          'Screeners review papers based on title and abstract only, deciding whether each paper should proceed to full-text review. Multiple screeners can be assigned per paper to reduce individual bias.',
        whyItMatters:
          'Screening by title and abstract first dramatically reduces the number of papers needing full-text retrieval. In a typical systematic review, 60–80% of papers are excluded at this stage, making this the most important filtering step.',
        howItWorks: [
          'Papers are assigned to one or more screeners',
          'Each screener makes an Include or Exclude decision per paper',
          'Exclusion reasons are selected from the list defined in Setup',
          "Screener agreement is tracked via Cohen's Kappa",
          'Papers with conflicting decisions enter conflict resolution',
        ],
        prismaMapping:
          'This stage maps to the "Screening" phase of the PRISMA 2020 flow diagram: records screened and records excluded with reasons.',
        nextStage: 'Full-Text Retrieval',
      },
    ],
  },
  {
    stage: ProjectState.FULL_TEXT_RETRIEVAL,
    label: 'Full-Text Retrieval',
    sections: [
      {
        id: 'full-text-retrieval',
        heading: 'Full-Text Retrieval',
        shortHelp:
          'After title/abstract screening, retrieve the full text for included papers. Track which PDFs are available, still needed, or unavailable. PDFs can be uploaded directly or linked from external sources.',
        whyItMatters:
          'A significant number of papers that pass title/abstract screening may have inaccessible full texts. Tracking retrieval status helps the team focus effort on finding the papers that matter and documenting what could not be obtained.',
        howItWorks: [
          'Mark papers as PDF Available, Not Available, or other configurable statuses',
          'Upload PDFs directly into Scholaris (stored in the filesystem)',
          'View PDFs in the browser using the built-in viewer',
          'Metadata extraction via GROBID runs automatically on upload',
        ],
        prismaMapping:
          'This stage maps to the "Reports sought for retrieval" and "Reports not retrieved" items in the PRISMA 2020 flow diagram.',
        nextStage: 'Full-Text Screening',
      },
    ],
  },
  {
    stage: ProjectState.FULL_TEXT_SCREENING,
    label: 'Full-Text Screening',
    sections: [
      {
        id: 'full-text-screening',
        heading: 'Full-Text Screening',
        shortHelp:
          'Review full papers in depth, applying your inclusion and exclusion criteria to the complete text. Screeners annotate PDFs and record decisions with supporting evidence.',
        whyItMatters:
          'Full-text screening is where the most nuanced decisions happen. Having access to the complete paper, and the ability to annotate and share findings with the team, ensures decisions are well-informed and defensible.',
        howItWorks: [
          'Each paper is assigned to screeners for full-text review',
          'Screeners read the full paper and make Include or Exclude decisions',
          'In-browser PDF annotation lets screeners highlight and comment on relevant passages',
          'Disagreements between screeners trigger conflict resolution',
          'Exclusion reasons are recorded for every excluded paper',
        ],
        prismaMapping:
          'This stage maps to the "Reports assessed for eligibility" and "Reports excluded" items in the PRISMA 2020 flow diagram.',
        nextStage: 'Data Extraction',
      },
    ],
  },
  {
    stage: ProjectState.DATA_EXTRACTION,
    label: 'Data Extraction',
    sections: [
      {
        id: 'data-extraction',
        heading: 'Data Extraction',
        shortHelp:
          'Extract structured data from included papers using a custom questionnaire. Define your own questions: text, choice, numeric, and assign multiple extractors per paper with conflict resolution for disagreements.',
        whyItMatters:
          'Data extraction transforms heterogeneous paper findings into a structured dataset ready for synthesis. A well-designed extraction form ensures you capture every data point your research questions require, consistently across all papers.',
        howItWorks: [
          'Design an extraction questionnaire in Setup with text, choice, and numeric question types',
          'Assign extractors to each included paper',
          'Extractors read the paper and answer each question',
          'Evidence can be linked to specific PDF annotations using the claim annotator',
          'Conflicts between extractors are flagged and resolved',
        ],
        prismaMapping:
          'This stage corresponds to the "Data collection process" and "Data items" items in the PRISMA 2020 checklist (Items 10 and 11).',
        nextStage: 'Completion',
      },
    ],
  },
  {
    stage: ProjectState.COMPLETED,
    label: 'Completion',
    sections: [
      {
        id: 'completion',
        heading: 'Project Completion',
        shortHelp:
          'Once all stages are complete, Scholaris generates a read-only summary of your review. The PRISMA 2020 flow diagram is automatically populated from your screening decisions.',
        whyItMatters:
          'The completion stage provides a single source of truth for your final report. All decisions, exclusions, extractions, and annotations are frozen and accessible for audit, publication, or reuse.',
        howItWorks: [
          'View the PRISMA 2020 flow diagram auto-populated with your screening numbers',
          "Review agreement quality metrics (Cohen's Kappa)",
          'Export data for analysis in your preferred statistical tool',
          'The project is locked read-only; no further changes are allowed',
        ],
        prismaMapping:
          'The completed project produces a PRISMA 2020 flow diagram and checklist, ready for inclusion in your manuscript.',
      },
    ],
  },
];

export function findGuideSection(
  id: string,
): { stage: GuideStageContent; section: GuideSectionContent } | undefined {
  for (const stage of GUIDE_CONTENT) {
    const section = stage.sections.find((s) => s.id === id);
    if (section) {
      return { stage, section };
    }
  }
  return undefined;
}
